import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Card, CardContent } from '@mui/material';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const PLCCommandPropertyPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];

  const [focusedInput, setFocusedInput] = useState(null);
  const [parameterMappings, setParameterMappings] = useState({});

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'text') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });
      return foundTag ? {
        variableId: value,
        variableName: foundTag.tagName,
        variableType: 'T'
      } : null;
    }

    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : value,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });
      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Initialize data when component mounts or when data prop changes
  useEffect(() => {
    if (!data) return;

    const newMappings = {
      input1: parseVariableString(data.selectedTag, data.tagInputType),
      input2: parseVariableString(data.commandValue, data.valueInputType)
    };

    // Tag değilse input1'i temizle
    if (newMappings.input1 && newMappings.input1.variableType !== 'T') {
      newMappings.input1 = null;
    }

    console.log('Initialized PLC Command Mappings:', newMappings);
    setParameterMappings(newMappings);
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    // input1 için sadece tag tipini kabul et
    if (focusedInput === 'input1' && variable.type !== 'T') {
      dispatch(setToastMessage({
        type: 'warning',
        message: 'Tag alanı için sadece Tag tipi değişken seçilebilir.'
      }));
      return;
    }

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    setParameterMappings(prev => ({
      ...prev,
      [focusedInput]: {
        variableId,
        variableName,
        variableType
      }
    }));
  };

  const InputCard = ({ label, inputKey, mapping, tagOnly = false }) => {
    const isFocused = focusedInput === inputKey;

    return (
      <Card
        onClick={() => setFocusedInput(inputKey)}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {label}
            </Typography>
            {tagOnly && (
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: '#f0f0f0',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  color: '#666'
                }}
              >
                Tag Only
              </Typography>
            )}
          </Box>

          {mapping?.variableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(mapping.variableType),
              borderRadius: 1,
              fontSize: '0.9rem'
            }}>
              {mapping.variableName}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              {tagOnly ? 'No tag selected' : 'No value selected'}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSave = () => {
    const { input1, input2 } = parameterMappings;

    if (!input1 || !input2) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen tüm alanları doldurun.'
      }));
      return;
    }

    // Tag kontrolü
    if (input1.variableType !== 'T') {
      dispatch(setToastMessage({
        type: 'error',
        message: 'İlk alan için sadece Tag seçilebilir.'
      }));
      return;
    }

    const getVariableString = (mapping) => {
      if (!mapping) return '';
      switch (mapping.variableType) {
        case 'LV':
          return `lv_${mapping.variableName}`;
        case 'V':
          return mapping.variableId;
        case 'T':
          return mapping.variableId;
        case 'constant':
          return mapping.variableId || mapping.variableName;
        default:
          return mapping.variableName;
      }
    };

    const title = `PLC Command: ${input1.variableName} = ${input2.variableName}`;

    const nodeData = {
      selectedTag: getVariableString(input1),
      commandValue: getVariableString(input2),
      tagInputType: input1.variableType === 'constant' ? 'text' : 'select',
      valueInputType: input2.variableType === 'constant' ? 'text' : 'select',
      label: 'PLC Command',
      Title: title,
    };

    console.log('Saving PLC Command Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'PLC Command ayarları kaydedildi.'
    }));
  };

  if (nodeType !== "PLCCommand") return null;

  return (
    <PanelShell title="PLC Komutu" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Variable Selector */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            {focusedInput === 'input1' ? 'Tag Seçimi' : 'Değişken / Sabit Seçimi'}
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={handleVariableSelect}
            placeholder={focusedInput === 'input1' ? "Tag ara..." : "Tag, değişken veya sabit ara..."}
            showConstants={focusedInput === 'input2'}
            filterTypes={focusedInput === 'input1' ? ['T'] : ['T', 'V', 'LV']}
          />
          {focusedInput && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
              Seçilen alan: {focusedInput === 'input1' ? 'Tag' : 'Değer'}
            </Typography>
          )}
        </Box>

        {/* Input Parameters */}
        <Box>
          <InputCard
            label="Tag"
            inputKey="input1"
            mapping={parameterMappings.input1}
            tagOnly={true}
          />

          <InputCard
            label="Değer"
            inputKey="input2"
            mapping={parameterMappings.input2}
          />
        </Box>
      </Box>
    </PanelShell>
  );
};

export default PLCCommandPropertyPanel;
